import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Tabs,
  Tab,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment
} from '@mui/material';
import GoogleMapsAddressInput from '../GoogleMapsAddressInput';
import EmbeddedNotesSection from './EmbeddedNotesSection';
import config from '../../config';
import BUSINESS_TYPES from '../../constants/businessTypes';
import { REGIONAL_DATA, getStates, getGreaterRegions, getSubRegions } from '../../constants/regionalData';

const OrganizationDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  isEdit, 
  organization = null,
  token,
  hideSalesConsultant = false,
  defaultSalesConsultantId = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    business_type: '',
    primary_name: '',
    primary_email: '',
    primary_phone: '',
    primary_general_email: '',
    accounts_name: '',
    accounts_email: '',
    accounts_phone: '',
    accounts_general_email: '',
    principal_name: '',
    principal_email: '',
    principal_phone: '',
    principal_general_email: '',
    business_manager_name: '',
    business_manager_email: '',
    business_manager_phone: '',
    business_manager_general_email: '',
    address: '',
    state: '',
    suburb: '',
    greaterRegion: '',
    subRegion: '',
    postcode: '',
    payment_terms: 7,
    notes: '',
    bad_debt: false,
    business_closed: false,
    is_active: true,
    is_school: false,
    is_business: false,
    sales_consultant_id: null,
    generalEmail: '',
    generalPhone: '',
    sameAsStreetAddress: true,
    postalAddress: '',
    numberOfFamilies: 0,
    schoolStatus: 'Active',
    schoolType: '',
    paymentModel: 'Ad Supported',
    adsEnabled: true, // Default to true since default paymentModel is 'Ad Supported'
    hasSchoolLink: false,
    hasEPublisher: false,
    additionalEPublisherAccount: false,
    hasPaper: false,
    website: '',
    lastInvoiceDate: '',
    agreementStartDate: '',
    agreementEndDate: '',
    schoolLinkSubscribers: 0,
    activeSchoolLinkAds: 0,
    deploymentsPerTerm: 0,
    ePublisherSubscribers: 0,
    ePublisherCost: 0,
    activeEPublisherAds: 0,
    paperCost: 0,
    paperType: '',
    activePaperAds: 0,
    advertisingObjectives: '',
    businessStatus: 'Active',
  });
  
  const [tabValue, setTabValue] = useState(0);
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [salesConsultants, setSalesConsultants] = useState([]);
  
  // Fetch sales consultants when dialog opens
  useEffect(() => {
    const fetchSalesConsultants = async () => {
      if (open && token) {
        try {
          const response = await fetch(`${config.apiUrl}/api/users?limit=100`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (response.ok) {
            const result = await response.json();
            const users = result.data || result;
            const consultants = users.filter(user => 
              user.role === 'sales' || user.role === 'admin' || user.role === 'manager'
            );
            setSalesConsultants(consultants);
          }
        } catch (error) {
          console.error('Error fetching sales consultants:', error);
        }
      }
    };
    fetchSalesConsultants();
  }, [open, token]);
  
  // Update form data when editing an organization or creating with initial data
  useEffect(() => {
    if (organization && (isEdit || (!isEdit && organization))) {
      const is_school = isValueTrue(organization.isSchool || organization.is_school);
      const is_business = isValueTrue(organization.isBusiness || organization.is_business);
      const is_active = isValueTrue(organization.isActive || organization.is_active);
      const bad_debt = isValueTrue(organization.badDebt || organization.bad_debt);
      const business_closed = isValueTrue(organization.businessClosed || organization.business_closed);
      // paid=0 means ad-supported (ads enabled), paid=1 means paid subscription (ads disabled)
      // If adsEnabled is explicitly set use it; otherwise invert the paid flag
      const adsEnabled = organization.adsEnabled !== undefined && organization.adsEnabled !== null
        ? isValueTrue(organization.adsEnabled)
        : organization.paid !== undefined && organization.paid !== null
          ? organization.paid === 0 || organization.paid === '0' || organization.paid === false
          : true;
      const hasSchoolLink = isValueTrue(organization.hasSchoolLink);
      const hasEPublisher = isValueTrue(organization.hasEPublisher);
      const hasPaper = isValueTrue(organization.hasPaper);
      
      setFormData({
        id: organization.id,
        name: organization.name || '',
        business_type: organization.businessType || organization.business_type || '',
        primary_name: organization.primaryName || organization.primary_name || '',
        primary_email: organization.primaryEmail || organization.primary_email || '',
        primary_phone: organization.primaryPhone || organization.primary_phone || '',
        primary_general_email: organization.primaryGeneralEmail || '',
        accounts_name: organization.accountsName || '',
        accounts_email: organization.accountsEmail || '',
        accounts_phone: organization.accountsPhone || '',
        accounts_general_email: organization.accountsGeneralEmail || '',
        principal_name: organization.principalName || '',
        principal_email: organization.principalEmail || '',
        principal_phone: organization.principalPhone || '',
        principal_general_email: organization.principalGeneralEmail || '',
        business_manager_name: organization.businessManagerName || '',
        business_manager_email: organization.businessManagerEmail || '',
        business_manager_phone: organization.businessManagerPhone || '',
        business_manager_general_email: organization.businessManagerGeneralEmail || '',
        website: organization.website || '',
        address: organization.address || '',
        state: organization.state || '',
        suburb: organization.suburb || '',
        greaterRegion: organization.greaterRegion || organization.region || '',
        subRegion: organization.subRegion || organization.region2 || '',
        postcode: organization.postcode || '',
        formattedAddress: organization.formattedAddress || organization.address || '',
        latitude: organization.latitude || null,
        longitude: organization.longitude || null,
        payment_terms: organization.paymentTerms ?? organization.payment_terms ?? 7,
        notes: organization.notes || '',
        is_school,
        is_business,
        is_active,
        bad_debt,
        business_closed,
        adsEnabled,
        sales_consultant_id: organization.salesConsultant?.id || organization.sales_consultant_id || defaultSalesConsultantId || null,
        generalEmail: organization.generalEmail || '',
        generalPhone: organization.generalPhone || '',
        sameAsStreetAddress: isValueTrue(organization.sameAsStreetAddress),
        postalAddress: organization.postalAddress || '',
        numberOfFamilies: organization.numberOfFamilies || 0,
        schoolStatus: organization.schoolStatus || 'Active',
        schoolType: organization.schoolType || '',
        paymentModel: organization.paymentModel || 'Ad Supported',
        hasSchoolLink,
        hasEPublisher,
        additionalEPublisherAccount: isValueTrue(organization.additionalEPublisherAccount),
        hasPaper,
        lastInvoiceDate: organization.lastInvoiceDate ? organization.lastInvoiceDate.split('T')[0] : '',
        agreementStartDate: organization.agreementStartDate ? organization.agreementStartDate.split('T')[0] : '',
        agreementEndDate: organization.agreementEndDate ? organization.agreementEndDate.split('T')[0] : '',
        schoolLinkSubscribers: organization.schoolLinkSubscribers || 0,
        activeSchoolLinkAds: organization.activeSchoolLinkAds || 0,
        deploymentsPerTerm: organization.deploymentsPerTerm || 0,
        ePublisherSubscribers: organization.ePublisherSubscribers || 0,
        // If no cost is stored, auto-derive it from deployments / subscribers so the
        // field isn't shown as $0 for schools that already have that data.
        ePublisherCost: (() => {
          const stored = parseFloat(organization.ePublisherCost);
          if (stored > 0) return stored;
          const deps  = parseInt(organization.deploymentsPerTerm, 10) || 0;
          const subs  = parseInt(organization.ePublisherSubscribers, 10);
          const effSubs = (!subs && organization.numberOfFamilies)
            ? parseInt(organization.numberOfFamilies, 10) * 2
            : subs || 0;
          if (deps > 0 && effSubs > 0) {
            const reach = (deps * 4) * effSubs;
            return reach > 5000 ? parseFloat((900 + (reach - 5000) * 0.025).toFixed(2)) : 900;
          }
          return 0;
        })(),
        activeEPublisherAds: organization.activeEPublisherAds || 0,
        paperCost: organization.paperCost || 0,
        paperType: organization.paperType || '',
        activePaperAds: organization.activePaperAds || 0,
        advertisingObjectives: organization.advertisingObjectives || '',
        businessStatus: organization.businessStatus || 'Active',
      });
      
      if (organization.additionalContacts) {
        setAdditionalContacts(organization.additionalContacts);
      } else {
        setAdditionalContacts([]);
      }
    } else {
      // Reset form data for new organization
      setFormData({
        name: '',
        business_type: '',
        primary_name: '',
        primary_email: '',
        primary_phone: '',
        primary_general_email: '',
        accounts_name: '',
        accounts_email: '',
        accounts_phone: '',
        accounts_general_email: '',
        principal_name: '',
        principal_email: '',
        principal_phone: '',
        principal_general_email: '',
        business_manager_name: '',
        business_manager_email: '',
        business_manager_phone: '',
        business_manager_general_email: '',
        address: '',
        state: '',
        suburb: '',
        greaterRegion: '',
        subRegion: '',
        postcode: '',
        payment_terms: 7,
        notes: '',
        bad_debt: false,
        business_closed: false,
        is_active: true,
        is_school: false,
        is_business: false,
        sales_consultant_id: defaultSalesConsultantId || null,
        generalEmail: '',
        generalPhone: '',
        website: '',
        sameAsStreetAddress: true,
        postalAddress: '',
        numberOfFamilies: 0,
        schoolStatus: 'Active',
        schoolType: '',
        paymentModel: 'Ad Supported',
        adsEnabled: true, // Default to true since default paymentModel is 'Ad Supported'
        hasSchoolLink: false,
        hasEPublisher: false,
        additionalEPublisherAccount: false,
        hasPaper: false,
        lastInvoiceDate: '',
        agreementStartDate: '',
        agreementEndDate: '',
        schoolLinkSubscribers: 0,
        activeSchoolLinkAds: 0,
        deploymentsPerTerm: 0,
        ePublisherSubscribers: 0,
        ePublisherCost: 0,
        activeEPublisherAds: 0,
        paperCost: 0,
        paperType: '',
        activePaperAds: 0,
        advertisingObjectives: '',
        businessStatus: 'Active',
      });
      
      setAdditionalContacts([]);
    }
  }, [isEdit, organization, defaultSalesConsultantId]);
  
  // Helper function to check if a value should be considered true
  const isValueTrue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
    if (typeof value === 'number') return value !== 0;
    return false;
  };
  
  // Handle website field blur to add https:// if not present
  const handleWebsiteBlur = (e) => {
    const value = e.target.value;
    if (value && !value.match(/^https?:\/\//)) {
      setFormData({
        ...formData,
        website: `https://${value}`
      });
    }
  };
  
  // Handle form input changes
  // ── ePublisher cost auto-calculation ─────────────────────────────────────
  // Formula (same as OfferForm): reach = (deploymentsPerTerm × 4) × effectiveSubscribers
  //   effectiveSubscribers = ePublisherSubscribers if > 0, else numberOfFamilies × 2
  //   cost = reach > 5000 ? 900 + (reach − 5000) × 0.025 : 900
  // Returns 0 when there isn't enough data to produce a meaningful number.
  const calculateEPublisherCost = (deploymentsPerTerm, ePublisherSubscribers, numberOfFamilies) => {
    const deployments = parseInt(deploymentsPerTerm, 10) || 0;
    const explicitSubs = parseInt(ePublisherSubscribers, 10);
    const effectiveSubs = (!explicitSubs && numberOfFamilies)
      ? parseInt(numberOfFamilies, 10) * 2
      : explicitSubs || 0;
    if (deployments <= 0 || effectiveSubs <= 0) return 0;
    const reach = (deployments * 4) * effectiveSubs;
    return reach > 5000 ? parseFloat((900 + (reach - 5000) * 0.025).toFixed(2)) : 900;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    // When any of the three ePublisher-cost inputs change, recalculate the cost
    // so it always stays in sync with deployments / subscribers.
    const ePubTriggers = ['deploymentsPerTerm', 'ePublisherSubscribers', 'numberOfFamilies'];
    if (ePubTriggers.includes(name)) {
      const updated = { ...formData, [name]: newValue };
      const computedCost = calculateEPublisherCost(
        updated.deploymentsPerTerm,
        updated.ePublisherSubscribers,
        updated.numberOfFamilies
      );
      setFormData({ ...updated, ePublisherCost: computedCost });
      return;
    }

    setFormData({
      ...formData,
      [name]: newValue,
    });
  };
  
  // Handle coordinate data from Google Maps
  const handleCoordinateChange = (addressData) => {
    setFormData(prev => {
      const newState = {...prev};
      
      newState.formattedAddress = addressData.fullAddress;
      
      if (addressData.coordinates && addressData.coordinates.lat && addressData.coordinates.lng) {
        newState.latitude = addressData.coordinates.lat;
        newState.longitude = addressData.coordinates.lng;
      }
      
      if (addressData.streetAddress) {
        newState.address = addressData.streetAddress;
      }
      
      // Auto-populate State, Greater Region, and Sub-Region from Google Maps
      if (addressData.state) {
        newState.state = addressData.state;
      } else if (addressData.region) {
        newState.state = addressData.region;
      }
      
      if (addressData.greaterRegion) {
        newState.greaterRegion = addressData.greaterRegion;
      }
      
      if (addressData.subRegion) {
        newState.subRegion = addressData.subRegion;
      }
      
      if (addressData.suburb) {
        newState.suburb = addressData.suburb;
      }
      
      if (addressData.postcode) {
        newState.postcode = addressData.postcode;
      }
      
      // Log the auto-populated regional data
      if (addressData.greaterRegion || addressData.subRegion) {
      }
      
      return newState;
    });
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle form submission
  const handleSubmit = () => {
    // Validate that at least one organization type is selected
    if (!formData.is_school && !formData.is_business) {
      alert('Please select at least one organization type (School or Business)');
      return;
    }
    
    const submitData = {
      ...formData,
      additionalContacts
    };
    onSubmit(submitData);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isEdit ? 'Edit Organisation' : 'Add Organisation'}
          {isEdit && organization?.xero_contact_id && (
            <Chip
              size="small"
              label={organization.xero_synced_at
                ? `Xero synced ${new Date(organization.xero_synced_at).toLocaleDateString('en-AU')}`
                : 'Xero synced'}
              color="success"
              variant="outlined"
              sx={{ fontSize: 11 }}
            />
          )}
          {isEdit && !organization?.xero_contact_id && (
            <Chip size="small" label="Not synced to Xero" color="default" variant="outlined" sx={{ fontSize: 11 }} />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="organization tabs"
          >
            <Tab label="Basic Info" />
            <Tab label="Contact Details" />
            <Tab label="Address" />
            <Tab label="Additional Info" />
          </Tabs>
        </Box>
        
        {/* Basic Info Tab */}
        {tabValue === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Organisation Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                variant="outlined"
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Organisation Type (can select both) <Typography component="span" color="error">*</Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                At least one type must be selected
              </Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_school}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          is_school: e.target.checked
                        });
                      }}
                      name="is_school"
                    />
                  }
                  label="School"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_business}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          is_business: e.target.checked
                        });
                      }}
                      name="is_business"
                    />
                  }
                  label="Business"
                />
              </FormGroup>
            </Grid>
            
            {/* School-specific fields */}
            {formData.is_school && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>School Type</InputLabel>
                    <Select
                      name="schoolType"
                      value={formData.schoolType}
                      onChange={handleInputChange}
                      label="School Type"
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Private">Private</MenuItem>
                      <MenuItem value="State">State</MenuItem>
                      <MenuItem value="Catholic">Catholic</MenuItem>
                      <MenuItem value="P&C">P&C</MenuItem>
                      <MenuItem value="Public">Public</MenuItem>
                      <MenuItem value="Primary">Primary</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                      <MenuItem value="College">College</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>School Status</InputLabel>
                    <Select
                      name="schoolStatus"
                      value={formData.schoolStatus}
                      onChange={handleInputChange}
                      label="School Status"
                    >
                      <MenuItem value="Reorder">Reorder</MenuItem>
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Ads Enabled
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.adsEnabled ? 'yes' : 'no'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setFormData({
                          ...formData,
                          adsEnabled: newValue === 'yes'
                        });
                      }
                    }}
                    aria-label="ads enabled status"
                    fullWidth
                  >
                    <ToggleButton value="yes" aria-label="yes">
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" aria-label="no">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Costing Type</InputLabel>
                    <Select
                      name="paymentModel"
                      value={formData.paymentModel}
                      onChange={(e) => {
                        const newPaymentModel = e.target.value;
                        
                        // Set Ads Enabled based on Costing Type
                        let adsEnabled = formData.adsEnabled; // Keep current value by default
                        
                        if (newPaymentModel === 'Ad Supported') {
                          adsEnabled = true; // Ad Supported ? Ads Enabled = Yes
                        } else if (newPaymentModel === 'Paid') {
                          adsEnabled = false; // Paid ? Ads Enabled = No
                        } else if (newPaymentModel === 'Free') {
                          adsEnabled = true; // Free ? Ads Enabled = Yes
                        }
                        
                        setFormData({
                          ...formData,
                          paymentModel: newPaymentModel,
                          adsEnabled: adsEnabled
                        });
                      }}
                      label="Costing Type"
                    >
                      <MenuItem value="Ad Supported">Ad Supported</MenuItem>
                      <MenuItem value="Paid">Paid</MenuItem>
                      <MenuItem value="Free">Free</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>State</InputLabel>
                    <Select
                      name="state"
                      value={formData.state}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          state: e.target.value,
                          greaterRegion: '', // Reset greater region when state changes
                          subRegion: '' // Reset sub-region when state changes
                        });
                      }}
                      label="State"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {getStates().map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal" disabled={!formData.state}>
                    <InputLabel>Greater Region</InputLabel>
                    <Select
                      name="greaterRegion"
                      value={formData.greaterRegion}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          greaterRegion: e.target.value,
                          subRegion: '' // Reset sub-region when greater region changes
                        });
                      }}
                      label="Greater Region"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {formData.state && getGreaterRegions(formData.state).map((region) => (
                        <MenuItem key={region} value={region}>
                          {region}
                        </MenuItem>
                      ))}
                    </Select>
                    {!formData.state && (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.5 }}>
                        Select a state first
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal" disabled={!formData.state || !formData.greaterRegion}>
                    <InputLabel>Sub-Region</InputLabel>
                    <Select
                      name="subRegion"
                      value={formData.subRegion}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          subRegion: e.target.value
                        });
                      }}
                      label="Sub-Region"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {formData.state && formData.greaterRegion && getSubRegions(formData.state, formData.greaterRegion).map((subRegion) => (
                        <MenuItem key={subRegion} value={subRegion}>
                          {subRegion}
                        </MenuItem>
                      ))}
                    </Select>
                    {!formData.greaterRegion && (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.5 }}>
                        Select a greater region first
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="lastInvoiceDate"
                    label="Last Invoice Date"
                    type="date"
                    value={formData.lastInvoiceDate}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="agreementStartDate"
                    label="Agreement Start Date"
                    type="date"
                    value={formData.agreementStartDate}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="agreementEndDate"
                    label="Agreement End Date"
                    type="date"
                    value={formData.agreementEndDate}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            
            {/* Business-specific fields */}
            {formData.is_business && (
              <>
                {!hideSalesConsultant && (
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined" margin="normal">
                      <InputLabel>Sales Consultant</InputLabel>
                      <Select
                        name="sales_consultant_id"
                        value={formData.sales_consultant_id || ''}
                        onChange={handleInputChange}
                        label="Sales Consultant"
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {salesConsultants.map((consultant) => (
                          <MenuItem key={consultant.id} value={consultant.id}>
                            {consultant.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Autocomplete
                    value={formData.business_type}
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        business_type: newValue || ''
                      });
                    }}
                    inputValue={formData.business_type}
                    onInputChange={(event, newInputValue) => {
                      setFormData({
                        ...formData,
                        business_type: newInputValue
                      });
                    }}
                    options={BUSINESS_TYPES}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Business Type"
                        variant="outlined"
                        margin="normal"
                        fullWidth
                      />
                    )}
                    freeSolo
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Business Status</InputLabel>
                    <Select
                      name="businessStatus"
                      value={formData.businessStatus}
                      onChange={handleInputChange}
                      label="Business Status"
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <TextField
                name="website"
                label="Website"
                value={formData.website}
                onChange={handleInputChange}
                onBlur={handleWebsiteBlur}
                fullWidth
                variant="outlined"
                margin="normal"
                placeholder="https://example.com"
              />
            </Grid>
          </Grid>
        )}
        
        {/* Contact Details Tab */}
        {tabValue === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Primary Contact</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="primary_name"
                label="Name"
                value={formData.primary_name}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="primary_phone"
                label="Phone"
                value={formData.primary_phone}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="primary_email"
                label="Email"
                value={formData.primary_email}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                type="email"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="primary_general_email"
                label="General Email"
                value={formData.primary_general_email}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                type="email"
              />
            </Grid>
            
            {/* Only show Principal and Business Manager for schools */}
            {formData.is_school && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>Principal</Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="principal_name"
                    label="Name"
                    value={formData.principal_name}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="principal_phone"
                    label="Phone"
                    value={formData.principal_phone}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="principal_email"
                    label="Email"
                    value={formData.principal_email}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    type="email"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="principal_general_email"
                    label="General Email"
                    value={formData.principal_general_email}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    type="email"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>Business Manager</Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="business_manager_name"
                    label="Name"
                    value={formData.business_manager_name}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="business_manager_phone"
                    label="Phone"
                    value={formData.business_manager_phone}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="business_manager_email"
                    label="Email"
                    value={formData.business_manager_email}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    type="email"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="business_manager_general_email"
                    label="General Email"
                    value={formData.business_manager_general_email}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    type="email"
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>Accounts Contact</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="accounts_name"
                label="Name"
                value={formData.accounts_name}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="accounts_phone"
                label="Phone"
                value={formData.accounts_phone}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="accounts_email"
                label="Email"
                value={formData.accounts_email}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                type="email"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="accounts_general_email"
                label="General Email"
                value={formData.accounts_general_email}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                type="email"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>General Contact</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="generalEmail"
                label="General Email"
                value={formData.generalEmail}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                type="email"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="generalPhone"
                label="General Phone"
                value={formData.generalPhone}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
          </Grid>
        )}
        
        {/* Address Tab */}
        {tabValue === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Street Address
              </Typography>
              <GoogleMapsAddressInput 
                value={formData.address || ''} 
                onChange={(address) => setFormData({ ...formData, address })}
                onCoordinateChange={handleCoordinateChange}
                initialAddress={formData.address || ''}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Postal Address same as Street?
              </Typography>
              <ToggleButtonGroup
                value={formData.sameAsStreetAddress ? 'yes' : 'no'}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setFormData({
                      ...formData,
                      sameAsStreetAddress: newValue === 'yes'
                    });
                  }
                }}
                aria-label="postal address same"
                fullWidth
              >
                <ToggleButton value="yes" aria-label="yes">
                  Yes
                </ToggleButton>
                <ToggleButton value="no" aria-label="no">
                  No
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            
            {!formData.sameAsStreetAddress && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Postal Address
                </Typography>
                <GoogleMapsAddressInput 
                  value={formData.postalAddress || ''} 
                  onChange={(address) => setFormData({ ...formData, postalAddress: address })}
                  onCoordinateChange={(addressData) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      postalAddress: addressData.fullAddress,
                    }));
                  }}
                  initialAddress={formData.postalAddress || ''}
                />
              </Grid>
            )}
          </Grid>
        )}
        
        {/* Additional Info Tab */}
        {tabValue === 3 && (
          <Grid container spacing={2}>
            {formData.is_school && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">School Products</Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    SchoolLink
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.hasSchoolLink ? 'yes' : 'no'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setFormData({
                          ...formData,
                          hasSchoolLink: newValue === 'yes'
                        });
                      }
                    }}
                    aria-label="schoollink"
                    fullWidth
                  >
                    <ToggleButton value="yes" aria-label="yes">
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" aria-label="no">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    ePublisher
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.hasEPublisher ? 'yes' : 'no'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setFormData({
                          ...formData,
                          hasEPublisher: newValue === 'yes'
                        });
                      }
                    }}
                    aria-label="epublisher"
                    fullWidth
                  >
                    <ToggleButton value="yes" aria-label="yes">
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" aria-label="no">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                {formData.hasEPublisher && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Additional ePublisher Account
                    </Typography>
                    <ToggleButtonGroup
                      value={formData.additionalEPublisherAccount ? 'yes' : 'no'}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          setFormData({
                            ...formData,
                            additionalEPublisherAccount: newValue === 'yes'
                          });
                        }
                      }}
                      aria-label="additional-epublisher"
                      fullWidth
                    >
                      <ToggleButton value="yes" aria-label="yes">Yes</ToggleButton>
                      <ToggleButton value="no" aria-label="no">No</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                )}
                
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Paper
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.hasPaper ? 'yes' : 'no'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setFormData({
                          ...formData,
                          hasPaper: newValue === 'yes'
                        });
                      }
                    }}
                    aria-label="paper"
                    fullWidth
                  >
                    <ToggleButton value="yes" aria-label="yes">
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" aria-label="no">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="numberOfFamilies"
                    label="Number of Families"
                    type="number"
                    value={formData.numberOfFamilies}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="advertisingObjectives"
                    label="Advertising Objections"
                    value={formData.advertisingObjectives}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    multiline
                    rows={2}
                  />
                </Grid>
                
                {/* SchoolLink specific fields */}
                {formData.hasSchoolLink && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 2 }}>SchoolLink Details</Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="schoolLinkSubscribers"
                        label="SchoolLink Subscribers"
                        type="number"
                        value={formData.schoolLinkSubscribers}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="activeSchoolLinkAds"
                        label="Active Number of SchoolLink Ads"
                        type="number"
                        value={formData.activeSchoolLinkAds}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </>
                )}
                
                {/* ePublisher specific fields */}
                {formData.hasEPublisher && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 2 }}>ePublisher Details</Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="deploymentsPerTerm"
                        label="Deployments per Term"
                        type="number"
                        value={formData.deploymentsPerTerm}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="ePublisherSubscribers"
                        label="ePublisher Subscribers"
                        type="number"
                        value={formData.ePublisherSubscribers}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="ePublisherCost"
                        label="ePublisher Cost"
                        type="number"
                        value={formData.ePublisherCost}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: '$'
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="activeEPublisherAds"
                        label="Active Number of ePublisher Ads"
                        type="number"
                        value={formData.activeEPublisherAds}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </>
                )}
                
                {/* Paper specific fields */}
                {formData.hasPaper && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 2 }}>Paper Details</Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="paperCost"
                        label="Paper Cost"
                        type="number"
                        value={formData.paperCost}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: '$'
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="paperType"
                        label="Paper Type"
                        value={formData.paperType}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="activePaperAds"
                        label="Active Number of Paper Ads"
                        type="number"
                        value={formData.activePaperAds}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
            
            {/* Business-specific additional info */}
            {formData.is_business && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Business Information</Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="payment_terms"
                    label="Terms"
                    type="number"
                    value={formData.payment_terms}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    inputProps={{ min: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">days</InputAdornment>
                    }}
                    helperText="Days the client has to pay an invoice"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Bad Debt
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.bad_debt ? 'yes' : 'no'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setFormData({
                          ...formData,
                          bad_debt: newValue === 'yes'
                        });
                      }
                    }}
                    aria-label="bad debt"
                    fullWidth
                  >
                    <ToggleButton value="yes" aria-label="yes">
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" aria-label="no">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="lastInvoiceDate"
                    label="Last Invoice Date"
                    type="date"
                    value={formData.lastInvoiceDate}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Business Closed
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.business_closed ? 'yes' : 'no'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setFormData({
                          ...formData,
                          business_closed: newValue === 'yes'
                        });
                      }
                    }}
                    aria-label="business closed"
                    fullWidth
                  >
                    <ToggleButton value="yes" aria-label="yes">
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" aria-label="no">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <EmbeddedNotesSection
                organizationId={isEdit ? organization?.id : null}
                initialNotes={formData.notes}
                onNotesUpdate={(updatedNotes, newNote, options) => {
                }}
                title="Organisation Notes"
                compact={true}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationDialog;
